import { useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';

import { api, absoluteUrl } from '../api/client';
import type { PlacePhoto } from '../api/types';

/**
 * The provider's photographs of a place.
 *
 * OpenStreetMap has no pictures, so a place here is a name, a category and a
 * stay time — and choosing between "Zero Milestone" and "Q Street Northwest"
 * from that alone is guesswork. These come from the same enrichment cache as
 * the ratings and hours, and are absent for exactly the same reason: nobody
 * has looked the place up yet.
 *
 * A horizontal strip rather than a grid. There are rarely more than four, and
 * a strip leaves the things people actually decide on above the fold.
 */

export function PhotoStrip({
  photos,
  height = 140,
}: {
  photos: PlacePhoto[];
  height?: number;
}) {
  /*
   * A photograph that will not load is removed rather than left as a broken
   * frame. The signed URL behind each one expires, so this is a normal
   * outcome on a dialog left open, not an error worth reporting.
   */
  const [broken, setBroken] = useState<string[]>([]);
  const usable = photos.filter((p) => !broken.includes(p.url));

  if (usable.length === 0) return null;

  /*
   * Required by the provider's terms wherever a photograph is shown, and
   * deduplicated because four pictures by one person should credit them once.
   */
  const credits = [
    ...new Set(usable.flatMap((p) => p.attributions.map((a) => a.displayName))),
  ];

  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        sx={{ overflowX: 'auto', pb: 0.5, scrollSnapType: 'x mandatory' }}
      >
        {usable.map((photo) => (
          <Box
            key={photo.url}
            component="img"
            src={absoluteUrl(photo.url)}
            alt=""
            loading="lazy"
            onError={() => setBroken((b) => [...b, photo.url])}
            sx={{
              height,
              borderRadius: 1,
              flexShrink: 0,
              scrollSnapAlign: 'start',
              objectFit: 'cover',
              bgcolor: 'action.hover',
            }}
          />
        ))}
      </Stack>
      {credits.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          Photographs by {credits.join(', ')}
        </Typography>
      )}
    </Box>
  );
}

/**
 * The same strip, for a caller that has a place id rather than a loaded
 * record — the map's place panel, which is built from map features and never
 * fetched the details.
 *
 * Silent on failure by design: this decorates a panel whose real content is
 * the address and the actions beneath it, and an error message about missing
 * photographs would be louder than the photographs ever were.
 */
export function PlacePhotoStrip({
  poiId,
  height,
}: {
  poiId: string | null;
  height?: number;
}) {
  const [photos, setPhotos] = useState<PlacePhoto[]>([]);

  useEffect(() => {
    if (!poiId) { setPhotos([]); return; }

    let current = true;
    void api.placeDetails(poiId)
      .then((details) => { if (current) setPhotos(details.photos); })
      .catch(() => { if (current) setPhotos([]); });

    // A second place clicked while the first is still loading must not have
    // the first one's pictures land on top of it.
    return () => { current = false; };
  }, [poiId]);

  return <PhotoStrip photos={photos} {...(height !== undefined ? { height } : {})} />;
}
